/**
 * FeedbackTab.jsx — Settings ▸ Feedback (activity tool: admin + super_admin).
 *
 * The bug / idea box inbox. Anyone signed in submits from the bubble at the
 * bottom-right of every page (layout/FeedbackButton); each row carries the page
 * and device it came from. Tag it Bug / Idea, mark it done, reopen if needed.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeedbackList, useFeedbackCount, useUpdateFeedback } from '../../hooks/useFeedback';
import { useToast } from '../layout/Toast';
import { timeAgo } from '../../lib/webLeads';
import { cn } from '../../lib/utils';

const FILTERS = [
  { id: 'new', label: 'New' },
  { id: 'done', label: 'Done' },
  { id: 'all', label: 'All' },
];

const KINDS = [
  { id: 'bug', label: 'Bug', on: 'text-red-accent border-red-accent/40 bg-red-accent/10' },
  { id: 'idea', label: 'Idea', on: 'text-purple border-purple/40 bg-purple/10' },
];

const EMPTY = {
  new: 'Nothing new. Bugs and ideas sent from the bubble at the bottom-right of every page land here.',
  done: 'Nothing marked done yet.',
  all: 'No feedback yet. Anyone signed in can send a bug or idea from the bubble at the bottom-right of every page.',
};

const fmtFull = (ts) => (ts ? new Date(ts).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '');

// The page is whatever the submitting browser sent — only a same-origin path
// ("/jobs?id=…") becomes a link; anything else is shown as text.
const isAppPath = (p) => typeof p === 'string' && p.startsWith('/') && !p.startsWith('//');

function FeedbackRow({ row, onUpdate, busy }) {
  const done = row.status === 'done';

  return (
    <div className={cn('p-3 bg-surface border border-border rounded', done && 'opacity-70')}>
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex items-center gap-1 shrink-0">
          {KINDS.map((k) => {
            const active = row.kind === k.id;
            return (
              <button
                key={k.id}
                data-track="feedback.kind"
                disabled={busy}
                aria-pressed={active}
                title={active ? `Clear the ${k.label} tag` : `Tag as ${k.label}`}
                onClick={() => onUpdate(row, { kind: active ? null : k.id })}
                className={cn(
                  'px-1.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wide border transition-colors disabled:opacity-50',
                  active ? k.on : 'text-text-dim border-border hover:text-text-primary hover:border-text-dim',
                )}
              >
                {k.label}
              </button>
            );
          })}
        </div>
        {/* min width so on a phone the action wraps below instead of crushing the message */}
        <div className="text-sm text-text-primary flex-1 min-w-[10rem] whitespace-pre-wrap break-words">{row.message}</div>
        {done ? (
          <button data-track="feedback.reopen" disabled={busy} onClick={() => onUpdate(row, { status: 'new' })}
            className="ml-auto px-2.5 py-1 rounded text-xs border border-border text-text-dim hover:text-text-primary hover:border-accent disabled:opacity-50">
            Reopen
          </button>
        ) : (
          <button data-track="feedback.done" disabled={busy} onClick={() => onUpdate(row, { status: 'done' })}
            className="ml-auto px-2.5 py-1 bg-accent text-bg rounded text-xs font-medium hover:bg-accent-hover disabled:opacity-50">
            Mark done
          </button>
        )}
      </div>

      <div className="text-xs text-text-dim mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <span>{row.userName || 'Unknown'}</span>
        {row.page && (
          <>
            <span>·</span>
            {isAppPath(row.page) ? (
              <Link to={row.page} data-track="feedback.page" className="text-accent hover:underline break-all">{row.page}</Link>
            ) : (
              <span className="break-all">{row.page}</span>
            )}
          </>
        )}
        {row.device && (<><span>·</span><span>{row.device}</span></>)}
        <span>·</span>
        <span title={fmtFull(row.createdAt)}>{timeAgo(row.createdAt)}</span>
        {done && (
          <>
            <span>·</span>
            <span title={fmtFull(row.doneAt)}>Done{row.doneBy ? ` by ${row.doneBy}` : ''} {timeAgo(row.doneAt)}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default function FeedbackTab() {
  const [filter, setFilter] = useState('new');
  const { data: rows = [], isLoading, isError, error, refetch } = useFeedbackList(filter);
  const { data: count } = useFeedbackCount();
  const update = useUpdateFeedback();
  const { toast } = useToast();
  const newCount = count?.new || 0;

  const handleUpdate = async (row, patch) => {
    try {
      await update.mutateAsync({ id: row.id, ...patch });
    } catch (err) {
      toast(`Couldn't update: ${err.message}`, 'error');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">Feedback</h3>
        <p className="text-xs text-text-dim mt-0.5">
          Bugs and ideas from the bubble at the bottom-right of every page, newest first. Each one records the page
          it was sent from — click it to see what they saw.
        </p>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-track={`feedback.filter.${f.id}`}
            onClick={() => setFilter(f.id)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded transition-colors',
              filter === f.id
                ? 'bg-accent text-bg'
                : 'bg-surface border border-border text-text-dim hover:text-text-primary',
            )}
          >
            {f.label}{f.id === 'new' && newCount > 0 ? ` (${newCount})` : ''}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-text-dim text-sm">Loading feedback…</div>
      ) : isError ? (
        <div className="text-sm text-red-accent">
          Couldn't load feedback: {error?.message}{' '}
          <button data-track="feedback.retry" onClick={() => refetch()} className="underline hover:text-text-primary">Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="p-4 bg-surface border border-border rounded text-sm text-text-dim">{EMPTY[filter]}</div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <FeedbackRow
              key={row.id}
              row={row}
              onUpdate={handleUpdate}
              busy={update.isPending && update.variables?.id === row.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
